﻿import type {
  Fragment,
  FragmentState,
  CommandMessage,
  CommandPayloadByType,
  CommandMessageType,
} from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';
import { normalizeOrigin, stripCommandToken, FRAGMENT_CONFIG } from '@anddone/ajs-util';
import type { StateManagement } from '../StateManagement';
import type { FragmentRegistry } from '../fragment-registry/FragmentRegistry';

const logger = createLogger('SDK', 'OrchestratorCommandManager');

/**
 * Manages command creation, queueing, flushing, and postMessage transport.
 */
export class OrchestratorCommandManager {
  // Commands waiting for their target fragment to finish mounting, keyed by fragment id.
  readonly #pendingCommands = new Map<string, CommandMessage[]>();

  public constructor(
    private readonly dataState: StateManagement,
    private readonly fragmentRegistry: FragmentRegistry,
  ) {}

  /**
   * Queued commands for a fragment, if any.
   *
   * @param fragmentId - Fragment id
   * @returns readonly CommandMessage[] | undefined
   */
  public getPendingCommands(fragmentId: string): readonly CommandMessage[] | undefined {
    return this.#pendingCommands.get(fragmentId);
  }

  /**
   * Number of fragments that currently have queued commands.
   *
   * @returns number
   */
  public pendingFragmentCount(): number {
    return this.#pendingCommands.size;
  }

  /**
   * Drop every queued command for every fragment.
   *
   * @returns void
   */
  public clearAllPending(): void {
    this.#pendingCommands.clear();
  }

  /**
   * Create a command message with shared SDK context.
   *
   * @param type - Command message type
   * @param data - Optional data to include with the command
   * @returns CommandMessage
   */
  private createCommandMessage<Type extends CommandMessageType>(
    type: Type,
    data: CommandPayloadByType[Type],
  ): CommandMessage {
    // Stamps the per-session command token onto any command whose payload declares
    // a `commandToken` field. Detected via duck-typing so the logic stays
    // automatically in sync with the payload type definitions.
    // - `handshake-response` delivers the token to the fragment for the first time.
    // - Any other payload that carries a `commandToken` key (e.g. `update-fees-and-totals`)
    //   gets it stamped on every send so the fragment can validate it.
    const shouldStampToken = data !== null && typeof data === 'object' && 'commandToken' in data;

    const stampedData = shouldStampToken
      ? this.dataState.session.applyCommandToken(data as object)
      : data;

    const message = {
      type,
      timestamp: new Date().toISOString(),
      paymentIntentId: this.dataState.config.paymentIntentId,
      sessionId: this.dataState.session.id,
      paymentAttemptId: this.dataState.attempts.paymentAttemptId,
      data: stampedData,
    } as CommandMessage;

    logger.debug('command', `Created command message type="${type}"`, stripCommandToken(message));

    return message;
  }

  /**
   * Queue a command until the target fragment has finished loading and handshaken.
   *
   * @param fragment - Fragment instance
   * @param message - Command message to queue
   * @returns void
   */
  private queueCommand(fragment: Fragment, message: CommandMessage): void {
    const existingCommands = this.#pendingCommands.get(fragment.id) ?? [];

    /**
     * Status updates are snapshots. Keeping older queued status updates can cause
     * stale state to be replayed after the fragment becomes ready, so keep only
     * the latest pending status update for each fragment.
     */
    const nextCommands =
      message.type === 'status-update'
        ? existingCommands.filter(command => command.type !== 'status-update')
        : existingCommands;

    nextCommands.push(message);
    this.#pendingCommands.set(fragment.id, nextCommands);

    logger.debug('queue', `Queued "${message.type}" command for "${fragment.fragmentName}"`, {
      type: message.type,
      fragmentName: fragment.fragmentName,
      id: fragment.id,
      status: fragment.status,
      queuedCommandCount: nextCommands.length,
    });
  }

  /**
   * Flush queued commands for a fragment if it has finished loading.
   *
   * @param fragment - Fragment instance
   * @returns void
   */
  public flushPendingCommands(fragment: Fragment): void {
    if (!this.canSendCommandNow(fragment)) {
      return;
    }

    const commands = this.#pendingCommands.get(fragment.id);

    if (!commands?.length) {
      return;
    }

    logger.debug('queue', `Flushing ${commands.length} queued command(s)`, {
      fragmentName: fragment.fragmentName,
      id: fragment.id,
      status: fragment.status,
    });

    commands.forEach(command => {
      this.postCommandToFragment(fragment, command);
    });

    this.#pendingCommands.delete(fragment.id);
  }

  /**
   * Clear queued commands for a fragment.
   *
   * @param fragment - Fragment whose queued commands should be removed
   * @returns void
   */
  public clearPendingCommandsForFragment(fragment: Fragment): void {
    this.#pendingCommands.delete(fragment.id);

    logger.debug('queue', `Cleared queued commands for "${fragment.fragmentName}"`, {
      fragmentName: fragment.fragmentName,
      id: fragment.id,
    });
  }

  /**
   * Send a command to a specific fragment.
   *
   * Checks to see if the fragement is still loading. If so, queues the command. Otherwise
   *
   * @param fragment - Fragment instance to send the command to
   * @param type - Command message type
   * @param data - Optional data to include with the command
   * @returns void
   */
  public sendCommandToFragment<Type extends CommandMessageType>(
    fragment: Fragment,
    type: Type,
    data: CommandPayloadByType[Type],
  ): void {
    if (!fragment) {
      logger.warn('command', `Cannot send "${type}" command to null fragment`, {
        type,
      });
      return;
    }

    // Enforce the per-fragment command allowlist: refuse to send a command that
    // is not listed in the target fragment's `allowedCommands` configuration.
    const fragmentConfig = FRAGMENT_CONFIG.get(fragment.fragmentName);
    if (!fragmentConfig?.allowedCommands.includes(type)) {
      logger.warn(
        'command',
        `Refusing to send command "${type}" to "${fragment.fragmentName}" — command is not in its allowedCommands list`,
        {
          type,
          fragmentName: fragment.fragmentName,
          allowedCommands: fragmentConfig?.allowedCommands,
        },
      );
      return;
    }

    const message: CommandMessage = this.createCommandMessage(type, data);
    const allowedFragmentOrigin = this.dataState.config.allowedFragmentOrigin;

    logger.debug('command', `Sending "${type}" command to "${fragment.fragmentName}"`, {
      type,
      fragmentName: fragment.fragmentName,
      fragmentId: fragment.id,
      status: fragment.status,
      message: stripCommandToken(message),
      allowedFragmentOrigin,
      iframeSrc: fragment.iframe.src,
    });

    if (!this.canSendCommandNow(fragment)) {
      this.queueCommand(fragment, message);
      return;
    }

    // Send the current command first so that ordering-sensitive messages (such as
    // `handshake-response`) always arrive before any commands that were queued
    // while the fragment was still mounting.  Queued commands (e.g.
    // `update-fees-and-totals`) may depend on data delivered by `handshake-response`
    // (the commandToken), so the handshake must be received first.
    this.postCommandToFragment(fragment, message);
    this.flushPendingCommands(fragment);

    if (fragment !== this.fragmentRegistry.getOrchestrator()) {
      this.sendLogCommandToOrchestratorFragment(
        `Sent command to ${fragment.fragmentName} Fragment: ${JSON.stringify(stripCommandToken(message))}`,
      );
    }
  }

  /**
   * Execute postMessage to send command message to the fragment iframe.
   *
   * @param fragment - Fragment instance to send the command to
   * @param message - Command message
   * @returns void
   */
  private postCommandToFragment(fragment: Fragment, message: CommandMessage): void {
    if (!fragment.iframe.contentWindow) {
      logger.warn(
        'command',
        `Cannot send "${message.type}" command because iframe window is unavailable`,
        {
          type: message.type,
          fragmentName: fragment.fragmentName,
          id: fragment.id,
          status: fragment.status,
        },
      );

      return;
    }

    const targetOrigin = normalizeOrigin(fragment.iframe.src);
    const allowedOrigin = this.dataState.config.allowedFragmentOrigin;

    if (!targetOrigin) {
      logger.warn('command', 'Unable to resolve fragment iframe origin', {
        fragmentName: fragment.fragmentName,
        id: fragment.id,
        iframeSrc: fragment.iframe.src,
      });

      return;
    }

    if (targetOrigin !== allowedOrigin) {
      logger.warn('command', 'Refusing to post message to unexpected fragment origin', {
        type: message.type,
        fragmentName: fragment.fragmentName,
        id: fragment.id,
        iframeSrc: fragment.iframe.src,
        targetOrigin,
        allowedOrigin,
      });

      return;
    }

    logger.debug('command', `Posting "${message.type}" command to "${fragment.fragmentName}"`, {
      type: message.type,
      fragmentName: fragment.fragmentName,
      fragmentId: fragment.id,
      status: fragment.status,
      message: stripCommandToken(message),
      iframeSrc: fragment.iframe.src,
      targetOrigin,
    });

    try {
      fragment.iframe.contentWindow.postMessage(message, targetOrigin);
    } catch (error) {
      logger.error(
        'command',
        `Failed to post "${message.type}" command to "${fragment.fragmentName}"`,
        {
          type: message.type,
          fragmentName: fragment.fragmentName,
          id: fragment.id,
          status: fragment.status,
          iframeSrc: fragment.iframe.src,
          targetOrigin,
          error,
        },
      );
    }
  }

  /**
   * Helper function to send a log command to the orchestrator fragment.
   *
   * @param message - Log message string
   * @returns void
   */
  public sendLogCommandToOrchestratorFragment(message: string): void {
    const orchestratorFragment = this.fragmentRegistry.getOrchestrator();

    if (!orchestratorFragment) {
      logger.debug(
        'orchestrator-log',
        'Skipped log command because orchestrator fragment is not registered',
        {
          message,
        },
      );
      return;
    }

    this.sendCommandToFragment(orchestratorFragment, 'add-orchestrator-log', { message });
  }

  /**
   * Helper to send fragment status updates to the orchestrator fragment.
   *
   * @returns void
   */
  public sendStatusUpdateCommandToOrchestratorFragment(): void {
    const orchestratorFragment = this.fragmentRegistry.getOrchestrator();

    if (!orchestratorFragment) {
      logger.debug(
        'status-update',
        'Skipped status update because orchestrator fragment is not registered',
      );
      return;
    }

    const fragmentStates: FragmentState[] = this.fragmentRegistry.getFragmentStates();

    this.sendCommandToFragment(orchestratorFragment, 'status-update', {
      fragmentStates,
    });
  }

  /**
   * Helper to send the transaction assemble command to the orchestrator fragment.
   *
   * @returns void
   */
  public sendTransactionSubmitCommandToOrchestratorFragment(): void {
    const orchestratorFragment = this.fragmentRegistry.getOrchestrator();

    if (!orchestratorFragment) {
      logger.debug(
        'transaction',
        'Skipped transaction assemble command because orchestrator fragment is not registered',
      );
      return;
    }

    logger.info('transaction', 'Sending transaction-assemble command to orchestrator fragment');
    const payload = {
      appKey: this.dataState.config.appKey,
      expectedFragments: this.fragmentRegistry.getProcessableFragments().map(([, fragment]) => ({
        fragmentName: fragment.fragmentName,
        fragmentId: fragment.id,
      })),
    };
    this.sendCommandToFragment(orchestratorFragment, 'transaction-assemble', payload);
  }

  /**
   * Determine whether a fragment can receive commands.
   *
   * A fragment is only blocked while it is loading. Once it has completed the handshake and
   * moved to pending, it has a live receiver window even if its input value is
   * not yet valid/ready for submission.
   *
   * @param fragment - Fragment instance
   * @returns boolean
   */
  private canSendCommandNow(fragment: Fragment): boolean {
    return fragment.status !== 'mounting';
  }
}
